import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import cx from 'classnames';
import { FormattedMessage } from 'react-intl';
import Modal from 'react-modal';
import { useDropzone } from 'react-dropzone';

import CCEvents, { EventNames } from '@companycam/companycam-events';
import { createDocumentForProject } from 'actions';
import UploadService from 'services/upload-service';
import { DropDown } from 'components/shared/DropDown';
import { ActionLink } from 'components/shared';
import Text from 'components/shared/DesignSystem/SlabTransferPortal/Text';
import AccessManagementListItem, {
  accessManagementData,
  DOCUMENT_ACCESS_PERMS,
} from './AccessManagementListItem';
import { DOCUMENT_MAX_SIZE_BYTES, DOCUMENT_MAX_SIZE_MB } from 'constants/files';
import { canAccessFeature, FEATURES } from 'helpers/features';
import useCompanyBillingStatus from 'components/billing/useCompanyBillingStatus';

// #region styled-components
const SelectedListItem = styled.div`
  padding: 8px 8px 8px 16px;
  width: 100%;
`;

const FieldLabelContainer = styled.div`
  display: flex;
  align-items: center;
  margin: var(--cc_size_spacing_s) 0 var(--cc_size_spacing_s);
`;
// #endregion styled-components

function DocumentUploadModal({
  projectId,
  onRequestClose,
  onDocumentUploaded,
  onAllUploadsComplete,
}) {
  const dispatch = useDispatch();
  const [rejectedFiles, setRejectedFiles] = useState([]);
  const [total, setTotal] = useState(0);
  const [completed, setCompleted] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0.0);
  const [currentPermissionSelection, setCurrentPermissionSelection] = useState(
    accessManagementData[0],
  );
  const [isAccessManagementDisabled, setIsAccessManagementDisabled] =
    useState(false);

  const { featureSet } = useCompanyBillingStatus();

  const hasSignatures = canAccessFeature(
    featureSet.featureSet,
    FEATURES.DOCUMENT_SIGNING,
  );

  const onChange = (selection) => {
    setCurrentPermissionSelection(selection);
  };

  const handleUploadProgress = (progress) => {
    const fileProgress = progress.loaded / progress.total;
    const overallProgress = total - completed;
    const _uploadProgress = Math.round((fileProgress / overallProgress) * 100);
    setUploadProgress(_uploadProgress);
  };

  const uploadFiles = (files) => {
    const list = [...files];
    const file = list.shift();

    if (file) {
      new UploadService()
        .uploadFileToS3(file, handleUploadProgress)
        .then((fileUrl) => {
          setCompleted(completed + 1);
          dispatch(
            createDocumentForProject(
              projectId,
              fileUrl,
              file.name,
              currentPermissionSelection.value ===
                DOCUMENT_ACCESS_PERMS.LIMITED,
            ),
          ).then((doc) => {
            if (typeof onDocumentUploaded === 'function') {
              onDocumentUploaded(doc);
            }
          });
          uploadFiles(list);
        })
        .catch(() => {
          uploadFiles(list);
        });
    } else if (rejectedFiles.length === 0) {
      // Log add document event
      CCEvents.logEvent(EventNames.ADD_DOCUMENT);

      // Let parent know we're done
      if (typeof onAllUploadsComplete === 'function') {
        onAllUploadsComplete();
      }
    }
  };

  const onDrop = (acceptedFiles, _rejectedFiles) => {
    setRejectedFiles(_rejectedFiles);
    setTotal(total + acceptedFiles.length); // Allow for multiple drops
    setCompleted(completed); // Allow for multiple drops
    setIsAccessManagementDisabled(total + acceptedFiles.length > 0);

    uploadFiles(acceptedFiles);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx',
      ],
      'application/vnd.ms-excel.sheet.macroEnabled.12': ['.xlsm'],
      'application/vnd.apple.numbers': ['.numbers'],
      'application/vnd.apple.pages': ['.pages'],
      'application/vnd.autodesk.autocad_dwg': ['.dwg'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'text/csv': ['.csv'],
      'text/plain': ['.txt'],
      'video/mp4': ['.mp4'],
      'video/x-ms-wmv': ['.wmv'],
      'video/quicktime': ['.mov'],
      'video/webm': ['.webm'],
    },
    multiple: true,
    onDrop,
    maxSize: DOCUMENT_MAX_SIZE_BYTES,
  });

  const renderSignaturesFileRequirements = () => (
    <>
      {' '}
      <FormattedMessage
        id="Documents.upload.maxfilesizesignatures"
        description="DropboxSign file size restrictions"
        defaultMessage="If you'd like to get this document signed, upload as a .pdf or .docx file under 40MB and less than 500 pages."
      />
    </>
  );

  /* eslint-disable react/no-unstable-nested-components */
  const renderManageDocumentAccess = () => (
    <>
      <FieldLabelContainer>
        <Text size="s" color="subtle" family="heading" weight="bold">
          <FormattedMessage id="Documents.document.manageAccess" />
        </Text>
      </FieldLabelContainer>
      <DropDown
        style={{ height: '100px' }}
        data={accessManagementData}
        placeholder="Filter by Permission"
        onChange={onChange}
        selectedItem={currentPermissionSelection}
        showClear={false}
        showToolTip={false}
        disabled={isAccessManagementDisabled}
        customItem={({ item }) => {
          return <AccessManagementListItem item={item} />;
        }}
        customPlaceholder={(getToggleButtonProps) => {
          return (
            <SelectedListItem>
              <AccessManagementListItem
                item={currentPermissionSelection}
                {...getToggleButtonProps()}
              />
            </SelectedListItem>
          );
        }}
        filterOptions={false}
      />
    </>
  );

  const photoDropZoneClasses = cx('photo-drop-zone', {
    'photo-drop-zone-active': isDragActive,
  });

  return (
    <Modal
      isOpen
      ariaHideApp={false}
      onRequestClose={onRequestClose}
      contentLabel="Upload a photo"
      overlayClassName="modal"
      className="modal-content-wrap"
    >
      <ActionLink className="modal-close-button" onClick={onRequestClose}>
        <i className="mdi mdi-close" />
      </ActionLink>
      <form>
        <h3 className="modal-title">
          <FormattedMessage
            id="Documents.upload.addDocuments"
            defaultMessage="Add Documents"
            description="section header"
          />
        </h3>
        <span className="instructions">
          <FormattedMessage
            id="Documents.upload.maxfilesize"
            defaultMessage="Maximum file size of {MAX_SIZE}MB."
            values={{
              MAX_SIZE: DOCUMENT_MAX_SIZE_MB,
            }}
          />
          {hasSignatures ? renderSignaturesFileRequirements() : null}
        </span>
        {renderManageDocumentAccess()}
        <div
          {...getRootProps()}
          className={photoDropZoneClasses}
          name="document-drop-zone"
        >
          <input {...getInputProps()} />
          {total > 0 && completed === total && (
            <FormattedMessage
              id="Documents.upload.success"
              defaultMessage="All of your accepted files have successfully uploaded!"
              description="message to the user informing them that their upload was successful"
            />
          )}
          {total > 0 && completed !== total && (
            <div>
              <div className="upload-progress-bar">
                <span
                  style={{
                    width: `${uploadProgress}%`,
                  }}
                />
              </div>
              <p className="upload-status">
                <FormattedMessage
                  id="Documents.upload.uploadMessaging"
                  defaultMessage="Uploading {count} documents…"
                  values={{
                    count: total,
                  }}
                  description="message shown to the user while when documents are uploading"
                />
              </p>
            </div>
          )}
          {total === 0 && (
            <div>
              <FormattedMessage
                id="Documents.upload.dragAndDropMessage"
                defaultMessage="Drag & Drop document here or click to upload to this project."
                description="message shown to the user showing where the drag and drop enabled upload area is"
              />
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
}

DocumentUploadModal.propTypes = {
  projectId: PropTypes.number.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  onDocumentUploaded: PropTypes.func.isRequired,
  onAllUploadsComplete: PropTypes.func.isRequired,
};

export default DocumentUploadModal;
